import { Bell, Play, Volume2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { SOUND_OPTIONS, previewSound, type SoundPreference } from '@/lib/sounds'

interface NotificationCardProps {
  soundPreference: SoundPreference
  onSoundChange: (preference: SoundPreference) => void
}

export function NotificationCard({ soundPreference, onSoundChange }: NotificationCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="size-5 text-muted-foreground" />
          <div>
            <CardTitle>Notification Preferences</CardTitle>
            <CardDescription>Choose how you want to be alerted</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Volume2 className="size-4 text-muted-foreground" />
            <p className="text-sm font-medium">Notification sounds</p>
          </div>
          {SOUND_OPTIONS.map((option) => (
            <div
              key={option.id}
              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                soundPreference === option.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
              }`}
              onClick={() => onSoundChange(option.id)}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`size-4 rounded-full border-2 flex items-center justify-center ${
                    soundPreference === option.id
                      ? 'border-primary'
                      : 'border-muted-foreground'
                  }`}
                >
                  {soundPreference === option.id && (
                    <div className="size-2 rounded-full bg-primary" />
                  )}
                </div>
                <span className="font-medium">{option.label}</span>
              </div>
              {option.file && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    previewSound(option.id)
                  }}
                >
                  <Play className="size-4 mr-1" />
                  Preview
                </Button>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Sounds will play when you receive new messages. A 10-second cooldown prevents notification spam.
        </p>
      </CardContent>
    </Card>
  )
}
